/**
 * IconMultiSelectFilterConfig
 *
 * A filter configuration class that plugs into the SearchAndFilters system
 * (extends BaseFilter, same contract as MultiSelectFilterConfig) but renders
 * using the IconMultiSelect component instead of the plain react-select
 * dropdown used by MultiSelect.
 *
 * Use this when filter options need per-option icons or images — e.g. a
 * cloud-provider logo displayed alongside each benchmark name.
 *
 * Supports two render styles via the optional `renderStyle` prop:
 *   • MultiSelectRenderStyle.Dropdown (default) — renders IconMultiSelect,
 *     an icon-aware react-select dropdown.
 *   • MultiSelectRenderStyle.Badges — renders icon-aware pill badges, honouring
 *     `deselectOthers` / `deselectSpecific` exactly like MultiSelect does.
 *
 * MultiSelect.tsx is left completely untouched.
 */
import React from 'react';
import { Badge } from 'reactstrap';

import { BaseFilter, BaseFilterConfigParams } from './FilterTypes';
import IconMultiSelect, { Option as IconOption } from './IconMultiSelect';
import { MultiSelectRenderStyle } from './MultiSelect';
import SelectedFilterBadge from './SelectedFiltersBar/SelectedFilterBadge';

export class IconMultiSelectFilterConfig extends BaseFilter {
  options: IconOption[];
  renderStyle?: MultiSelectRenderStyle;

  constructor(
    params: BaseFilterConfigParams & {
      options: IconOption[];
      renderStyle?: MultiSelectRenderStyle;
    },
  ) {
    super({ ...params });
    this.options = params.options;
    this.renderStyle = params.renderStyle;
  }

  // ── Selection logic (mirrors MultiSelect.tsx updateSelectedValues) ─────────

  private computeUpdatedValues(
    current: string[],
    option: IconOption,
  ): string[] {
    const isSelected = current.includes(option.value);

    if (option.deselectOthers) {
      return isSelected ? [] : [option.value];
    }

    if (option.deselectSpecific) {
      return isSelected
        ? current.filter((v) => v !== option.value)
        : [
            ...current
              .filter((v) => !option.deselectSpecific?.includes(v))
              .filter(
                (v) =>
                  !this.options.find((o) => o.deselectOthers && o.value === v),
              ),
            option.value,
          ];
    }

    return isSelected
      ? current.filter((v) => v !== option.value)
      : [
          ...current.filter(
            (v) => !this.options.find((o) => o.deselectOthers && o.value === v),
          ),
          option.value,
        ];
  }

  private handleBadgeClick(
    option: IconOption,
    onSelectedValueChange: () => void,
  ): void {
    const current = (this.getSelectedValues() as string[]) ?? [];
    const updated = this.computeUpdatedValues(current, option);
    this.currentSelectedValues = updated.length > 0 ? updated : undefined;
    onSelectedValueChange();
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  private renderIconBadges(onSelectedValueChange: () => void): JSX.Element {
    const selected = (this.getSelectedValues() as string[]) ?? [];

    return (
      <div className="d-flex flex-wrap g-0">
        {this.options.map((option) => {
          const isSelected = selected.includes(option.value);
          return (
            <Badge
              key={option.value}
              color={isSelected ? 'primary' : 'secondary'}
              pill
              className="cursor-pointer mb-1 px-2 py-2"
              style={{
                fontSize: '0.80rem',
                cursor: 'pointer',
                fontWeight: 'normal',
              }}
              onClick={() =>
                this.handleBadgeClick(option, onSelectedValueChange)
              }
            >
              {option.imageSrc ? (
                <img
                  src={option.imageSrc}
                  alt={option.label}
                  style={{
                    width: 14,
                    height: 14,
                    objectFit: 'contain',
                    marginRight: 4,
                    verticalAlign: 'middle',
                  }}
                />
              ) : option.iconClass ? (
                <i className={option.iconClass} style={{ marginRight: 4 }} />
              ) : null}
              {option.label}
            </Badge>
          );
        })}
      </div>
    );
  }

  render(onSelectedValueChange: () => void): JSX.Element {
    if (this.renderStyle === MultiSelectRenderStyle.Badges) {
      return this.renderIconBadges(onSelectedValueChange);
    }

    // Default: icon-aware react-select dropdown.
    // When options are empty (e.g. filtered to zero by a dependent filter or
    // not yet loaded from API) render a clearly disabled dropdown so the UI
    // layout remains stable and the user understands why nothing is selectable.
    const isDisabled = this.options.length === 0;
    const selectedValues = (this.getSelectedValues() as string[]) ?? [];
    return (
      <IconMultiSelect
        options={this.options}
        selectedValues={Array.isArray(selectedValues) ? selectedValues : []}
        onChange={(values) => {
          // Store undefined (not []) when empty so that getSelectedValues()
          // falls through to defaultSelectedValues and
          // isSelectionDifferentFromDefault() returns false — the filter is
          // then excluded from getActiveFilterValues() and not sent to the API.
          this.currentSelectedValues = values.length > 0 ? values : undefined;
          onSelectedValueChange();
        }}
        placeholder={isDisabled ? 'No option available' : this.label}
        isDisabled={isDisabled}
      />
    );
  }

  // ── Filter badge (selected-filters bar) ────────────────────────────────────

  private getSelectedOptionLabels(): string[] {
    const selected = this.getSelectedValues() as string[];
    if (!Array.isArray(selected)) return [];
    return selected.map((value) => {
      const option = this.options.find((o) => o.value === value);
      return option ? option.label : value;
    });
  }

  renderFilterBadge(onSelectedValueChange: () => void): JSX.Element | null {
    const labels = this.getSelectedOptionLabels();
    if (labels.length === 0) return null;

    return (
      <SelectedFilterBadge
        key={this.id}
        label={this.label}
        values={labels}
        onRemove={() => {
          this.resetSelectionToDefault();
          onSelectedValueChange();
        }}
      />
    );
  }
}
